import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { apiLoadOtherSettings, apiSaveOtherSettings } from '../../api/otherSettingApi';
import { apiLoadRescheduleSettings, apiSaveRescheduleSettings } from '../../api/rescheduleSettingApi';
import { apiLoadRushSurchargeSettings, apiSaveRushSurchargeSettings } from '../../api/rushSurchargeSettingApi';
import { loadOtherSettings, updateOtherSetting } from '../../reducers/other-settings';
import { loadRescheduleSettings, updateRescheduleSetting } from '../../reducers/reschedule-settings';
import { loadRushSurchargeSettings, updateRushSurchargeSetting } from '../../reducers/rush-surcharge-settings';
import Configuration from '../../Configuration';
import CheckboxField from '../fields/CheckboxField';
import TextField from '../fields/TextField';
import Heading from '../Heading'
import Loading from '../Loading';
import SaveButton from '../SaveButton'
import TableRowOtherSetting from '../TableRowOtherSetting'

const OtherSettingsTab = () => {
  const [saving, setSaving] = useState(false);
  const isPremium = Configuration.getInstance().is_premium

  const dispatch = useDispatch();
  const loaded = useSelector(state => {
    return state.otherSettings.loaded;
  });
  // Per-order reschedule window (premium-only, feature #6) -- a different
  // underlying PHP settings group than the free "other settings" above, so
  // it gets its own slice/load call, loaded and saved alongside them.
  const rescheduleLoaded = useSelector(state => {
    return state.rescheduleSettings.loaded;
  });
  // Rush/same-day delivery surcharge (premium-only, feature #3b) -- another
  // distinct settings group, same load-alongside/save-alongside pattern as
  // the reschedule section above.
  const rushSurchargeLoaded = useSelector(state => {
    return state.rushSurchargeSettings.loaded;
  });
  useEffect(() => {
    if (!loaded) {
      apiLoadOtherSettings(response => {
        dispatch(loadOtherSettings(response));
      });
    }
    if (isPremium && !rescheduleLoaded) {
      apiLoadRescheduleSettings(response => {
        dispatch(loadRescheduleSettings(response));
      });
    }
    if (isPremium && !rushSurchargeLoaded) {
      apiLoadRushSurchargeSettings(response => {
        dispatch(loadRushSurchargeSettings(response));
      });
    }
  }, [loaded, isPremium, rescheduleLoaded, rushSurchargeLoaded]);
  const otherSettings = useSelector(state => {
    return state.otherSettings.data;
  });
  const rescheduleSettings = useSelector(state => {
    return state.rescheduleSettings.data;
  });
  const rushSurchargeSettings = useSelector(state => {
    return state.rushSurchargeSettings.data;
  });
  const handleChangeProperty = (value, propName) => {
    let data = {}
    data[propName] = value
    dispatch(updateOtherSetting(data, () => {}))
  }
  const handleChangeRescheduleProperty = (value, propName) => {
    let data = {}
    data[propName] = value
    dispatch(updateRescheduleSetting(data))
  }
  const handleChangeRushSurchargeProperty = (value, propName) => {
    let data = {}
    data[propName] = value
    dispatch(updateRushSurchargeSetting(data))
  }
  const handleSaveChanges = (e) => {
    e.preventDefault();
    setSaving(true);
    apiSaveOtherSettings(otherSettings, response => {
      setSaving(false);
    })
    if (isPremium) {
      apiSaveRescheduleSettings(rescheduleSettings, () => {})
      apiSaveRushSurchargeSettings(rushSurchargeSettings, () => {})
    }
  }
  return (
    <div className="dreamfox-tab">
      <Heading text="Other Settings" />
      {!loaded && <Loading message="Loading..." />}
      {loaded &&
        <div className="dreamfox-tab__content">
          <h3>Other Settings</h3>
          <table className="dreamfox-table-other">
            <tbody>
              <TableRowOtherSetting htmlFor="required" label="Delivery date a required">
                <CheckboxField options={{
                  name: 'required',
                  checked: otherSettings.required,
                  onChange: e => handleChangeProperty(e.target.checked, 'required')
                }} />
              </TableRowOtherSetting>
              <TableRowOtherSetting htmlFor="showEmptyOnCheckout" label="Show Empty Delivery date on Checkout Page default">
              <CheckboxField options={{
                name: 'showEmptyOnCheckout',
                checked: otherSettings.showEmptyOnCheckout,
                onChange: e => handleChangeProperty(e.target.checked, 'showEmptyOnCheckout')
              }} />
              </TableRowOtherSetting>
              <TableRowOtherSetting htmlFor="labelOnCheckout" label="Delivery Date Label on Checkout Page">
                <TextField options={{
                  name: "labelOnCheckout",
                  value: otherSettings.labelOnCheckout,
                  onChange: e => handleChangeProperty(e.target.value, 'labelOnCheckout')
                }} />
              </TableRowOtherSetting>
              <TableRowOtherSetting htmlFor="descriptionOnCheckout" label="Delivery Date Field Description on Checkout Page">
                <TextField options={{
                  name: "descriptionOnCheckout",
                  value: otherSettings.descriptionOnCheckout,
                  onChange: e => handleChangeProperty(e.target.value, 'descriptionOnCheckout')
                }} />
              </TableRowOtherSetting>
              {isPremium && rescheduleLoaded && <>
                <TableRowOtherSetting htmlFor="rescheduleEnabled" label="Allow customers to reschedule their own delivery">
                  <CheckboxField options={{
                    name: 'rescheduleEnabled',
                    checked: rescheduleSettings.enabled,
                    onChange: e => handleChangeRescheduleProperty(e.target.checked, 'enabled')
                  }} />
                </TableRowOtherSetting>
                <TableRowOtherSetting htmlFor="rescheduleWindowDays" label="Minimum days notice to reschedule">
                  <input
                    type="number"
                    min="0"
                    id="rescheduleWindowDays"
                    name="rescheduleWindowDays"
                    value={rescheduleSettings.windowDays}
                    onChange={e => {
                      const value = parseInt(e.target.value, 10)
                      handleChangeRescheduleProperty(isNaN(value) ? 0 : Math.max(0, value), 'windowDays')
                    }}
                  />
                </TableRowOtherSetting>
              </>}
              {isPremium && rushSurchargeLoaded && <>
                <TableRowOtherSetting htmlFor="rushThresholdDays" label="Rush/same-day surcharge threshold (days)">
                  <input
                    type="number"
                    min="0"
                    id="rushThresholdDays"
                    name="rushThresholdDays"
                    value={rushSurchargeSettings.thresholdDays}
                    onChange={e => {
                      const value = parseInt(e.target.value, 10)
                      handleChangeRushSurchargeProperty(isNaN(value) ? 0 : Math.max(0, value), 'thresholdDays')
                    }}
                  />
                </TableRowOtherSetting>
                <TableRowOtherSetting htmlFor="rushAmount" label="Rush/same-day surcharge amount">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    id="rushAmount"
                    name="rushAmount"
                    value={rushSurchargeSettings.amount}
                    onChange={e => {
                      const value = parseFloat(e.target.value)
                      handleChangeRushSurchargeProperty(isNaN(value) ? 0 : Math.max(0, value), 'amount')
                    }}
                  />
                </TableRowOtherSetting>
                <tr>
                  <td colSpan="2">
                    <small>Applies when the customer picks a delivery date fewer than the threshold days out. Set either field to 0 to disable.</small>
                  </td>
                </tr>
              </>}
            </tbody>
          </table>
          <SaveButton saving={saving} onClick={handleSaveChanges} />
        </div>
      }
    </div>
  )
}

export default OtherSettingsTab
